import { Box, DropdownLayout, IconButton, Input, InputArea, Popover } from '@wix/design-system';
import { isExperimentOn, isValidDomain } from '@wix/domains-serverless-interface';
import { ChevronRightLargeSmall, DismissSmall } from '@wix/wix-ui-icons-common/lazy';
import { Search } from '@wix/wix-ui-icons-common/odeditor';
import classnames from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useUnassignedDomainsList } from '../../hooks/useUnassignedDomainsList';
import { CoreDomainSearchDataHooks, CoreDomainSearchModule } from '../../module/module';
import { TypewriterPlaceholder } from '../../components/TypewriterPlaceholder';
import s from './SearchInput.scss';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { ExperimentSpecs } from '../../constants';
const INPUT_MAX_LENGTH = 100;
export const SearchInput = _ref => {
  let placeholder = _ref.placeholder,
    autoFocus = _ref.autoFocus,
    onBlur = _ref.onBlur,
    onFocus = _ref.onFocus,
    onClear = _ref.onClear,
    onSubmit = _ref.onSubmit,
    _ref$showOptionsIfEmp = _ref.showOptionsIfEmptyInput,
    showOptionsIfEmptyInput = _ref$showOptionsIfEmp === void 0 ? true : _ref$showOptionsIfEmp,
    hideUnassignedDomains = _ref.hideUnassignedDomains,
    multiline = _ref.multiline,
    showSearchIcon = _ref.showSearchIcon,
    submitIcon = _ref.submitIcon,
    emphasized = _ref.emphasized,
    hideClearButton = _ref.hideClearButton;
  const _CoreDomainSearchModu = CoreDomainSearchModule.SDK.domainInputStore,
    setQuery = _CoreDomainSearchModu.setQuery,
    setSubmittedDomain = _CoreDomainSearchModu.setSubmittedDomain,
    setSubmittedFilters = _CoreDomainSearchModu.setSubmittedFilters,
    useTlds = _CoreDomainSearchModu.useTlds,
    useStyle = _CoreDomainSearchModu.useStyle,
    setInputRef = _CoreDomainSearchModu.setInputRef,
    useKeywords = _CoreDomainSearchModu.useKeywords,
    useQuery = _CoreDomainSearchModu.useQuery,
    resetState = _CoreDomainSearchModu.resetState;
  const query = useQuery();
  const selectedTlds = useTlds();
  const selectedKeywords = useKeywords();
  const selectedStyle = useStyle();
  const _CoreDomainSearchModu2 = CoreDomainSearchModule.SDK.Layouts,
    InputContainer = _CoreDomainSearchModu2.InputContainer,
    ActionButton = _CoreDomainSearchModu2.ActionButton;
  const inputRef = useRef(null);
  const _ref2 = getPremiumEssentialsSafe() ?? {},
    _ref2$experiments = _ref2.experiments,
    experiments = _ref2$experiments === void 0 ? {} : _ref2$experiments;
  const isLoggedInConnect = isExperimentOn(experiments[ExperimentSpecs.LoggedInConnect]);
  const getNativeInput = () => {
    var _inputRef$current, _inputRef$current2;
    return multiline ? (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.textArea : (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.input;
  };
  const _useUnassignedDomains = useUnassignedDomainsList(domain => {
      var _getNativeInput;
      return domain == null || domain.includes == null ? void 0 : domain.includes(((_getNativeInput = getNativeInput()) == null ? void 0 : _getNativeInput.value) ?? '');
    }, hideUnassignedDomains),
    unassingedDomainsOptions = _useUnassignedDomains.unassingedDomainsOptions,
    isOpen = _useUnassignedDomains.isOpen,
    setIsOpen = _useUnassignedDomains.setIsOpen;
  const _useState = useState(true),
    isBlurred = _useState[0],
    setIsBlurred = _useState[1];
  const handleSubmit = () => {
    var _getNativeInput2, _inputRef$current3;
    setIsOpen(false);
    const value = ((_getNativeInput2 = getNativeInput()) == null ? void 0 : _getNativeInput2.value) ?? '';
    (_inputRef$current3 = inputRef.current) == null || _inputRef$current3.blur();
    setQuery(value);
    const filters = {
      query: value,
      tlds: selectedTlds ?? [],
      keywords: selectedKeywords,
      style: selectedStyle
    };
    if (isValidDomain(filters.query)) {
      setSubmittedDomain(filters.query);
    } else {
      setSubmittedDomain(undefined);
    }
    setSubmittedFilters(filters);
    onSubmit == null || onSubmit(filters);
  };
  const handleFocus = () => {
    onFocus == null || onFocus();
    setIsBlurred(false);
  };
  const handleBlur = () => {
    setIsBlurred(true);
    onBlur == null || onBlur();
  };
  useEffect(() => {
    setInputRef(inputRef.current);
  }, [inputRef, setInputRef]);
  return /*#__PURE__*/React.createElement(Popover, {
    shown: isOpen,
    dynamicWidth: true,
    moveBy: {
      x: 1,
      y: 1
    },
    appendTo: "viewport",
    fixed: true,
    className: s.popover,
    onClickOutside: () => setIsOpen(false)
  }, /*#__PURE__*/React.createElement(Popover.Element, null, /*#__PURE__*/React.createElement(InputContainer, {
    verticalAlign: multiline ? 'top' : 'middle'
  }, showSearchIcon && /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle",
    className: s.searchIcon
  }, /*#__PURE__*/React.createElement(Search, null)), /*#__PURE__*/React.createElement("div", {
    className: s.inputWrapper
  }, !multiline && isBlurred ? /*#__PURE__*/React.createElement(TypewriterPlaceholder, {
    placeholder: placeholder,
    query: query
  }) : null, multiline ? /*#__PURE__*/React.createElement(InputArea, {
    className: classnames(s.searchInputContainer, {
      [s.emphasizedInput]: emphasized
    }),
    ref: inputRef,
    autoFocus: autoFocus,
    defaultValue: query,
    placeholder: Array.isArray(placeholder) ? placeholder[0] : placeholder,
    maxLength: INPUT_MAX_LENGTH,
    autoGrow: true,
    minRowsAutoGrow: 1,
    resizable: false,
    onChange: e => setQuery(e.target.value),
    onKeyDown: e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    onFocus: () => {
      handleFocus();
      if (!isLoggedInConnect) {
        return;
      }
      if (showOptionsIfEmptyInput || query) {
        setIsOpen(true);
      }
    },
    onBlur: () => {
      handleBlur();
    },
    dataHook: CoreDomainSearchDataHooks.searchInput
  }) : /*#__PURE__*/React.createElement(Input, {
    className: classnames(s.searchInputContainer, s.singleLine, {
      [s.emphasizedInput]: emphasized
    }),
    ref: inputRef,
    autoSelect: false,
    autoFocus: autoFocus,
    defaultValue: query,
    clearButton: false,
    textOverflow: "ellipsis",
    maxLength: INPUT_MAX_LENGTH,
    onChange: e => setQuery(e.target.value),
    onEnterPressed: handleSubmit,
    onInputClicked: () => {
      if (showOptionsIfEmptyInput || query) {
        setIsOpen(true);
      }
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    menuArrow: false,
    dataHook: CoreDomainSearchDataHooks.searchInput
  })), /*#__PURE__*/React.createElement(Box, {
    gap: "3px",
    verticalAlign: "middle",
    marginTop: multiline ? emphasized ? '6px' : '4px' : undefined
  }, query && !hideClearButton && /*#__PURE__*/React.createElement(IconButton, {
    skin: "transparent",
    className: s.clearButton,
    dataHook: CoreDomainSearchModule.internals.dataHooks.clearInput,
    priority: "tertiary",
    size: "small",
    onClick: () => {
      var _inputRef$current7;
      resetState();
      setSubmittedDomain(undefined);
      onClear == null || onClear();
      if (multiline) {
        var _inputRef$current4, _inputRef$current5;
        if ((_inputRef$current4 = inputRef.current) != null && _inputRef$current4.textArea) {
          inputRef.current.textArea.value = '';
        }
        (_inputRef$current5 = inputRef.current) == null || _inputRef$current5.calculateComputedRows();
      } else {
        var _inputRef$current6;
        (_inputRef$current6 = inputRef.current) == null || _inputRef$current6.clear();
      }
      (_inputRef$current7 = inputRef.current) == null || _inputRef$current7.focus();
    }
  }, /*#__PURE__*/React.createElement(DismissSmall, {
    color: "black"
  })), /*#__PURE__*/React.createElement(ActionButton, {
    skin: "dark",
    size: "small",
    priority: "primary",
    onClick: handleSubmit,
    dataHook: CoreDomainSearchDataHooks.searchSubmitButton
  }, submitIcon ?? /*#__PURE__*/React.createElement(ChevronRightLargeSmall, {
    color: "white",
    className: s.submitIcon
  }))))), /*#__PURE__*/React.createElement(Popover.Content, null, /*#__PURE__*/React.createElement(DropdownLayout, {
    markedOption: false,
    visible: true,
    className: s.unassingedDomainsDropdown,
    maxHeightPixels: "225px",
    options: unassingedDomainsOptions,
    onSelect: option => {
      if (option.id && typeof option.id === 'string') {
        var _inputRef$current8;
        setQuery(option.id);
        const el = getNativeInput();
        if (el) {
          el.value = option.id;
        }
        (_inputRef$current8 = inputRef.current) == null || _inputRef$current8.focus();
      }
      setIsOpen(false);
    }
  })));
};
export default SearchInput;
//# sourceMappingURL=SearchInput.js.map